/**
 * Which half of the two-pass render owns a variable.
 *
 * A template bound for Mailcoach is rendered twice: once by us when we push it
 * (resolving the layout, the fragments, the theme and the partner's saved
 * texts), and once by Mailcoach at send time, against the subscriber's own
 * replacements. `{{ … }}` output tags need no help — the backend's
 * `SendTimeTags` swaps the provider's ones for markers across our render and
 * puts them back afterwards.
 *
 * A `{% … %}` statement needs the same treatment, and gets it two ways. What we
 * compile here is wrapped in `{% verbatim %}`: our render emits the statement as
 * text instead of running it, and Mailcoach — which renders the stored HTML
 * again — runs it for real, per recipient. It is the readable form of what the
 * legacy seeds do by hand with `{{'{%' }} … {{'%}'}}`, and it is what keeps the
 * *preview* honest too, since twig.js reads the same source. (`SendTimeTags`
 * also defers a bare send-time `{% if %}` on the push, which is what covers the
 * subject line and hand-written source, where there is no compiler to wrap
 * anything. Emitting `{% verbatim %}` here is still the right output: it says so
 * in the stored body rather than relying on a classification made later.)
 *
 * ### Where the answer comes from
 *
 * Not from here. `/email-variables` **declares** which namespaces our own render
 * resolves (`render_time_namespaces`), and the server answers that from the same
 * place the push does. This module only applies the declaration, so there is one
 * statement of the fact rather than two derivations of it that can disagree —
 * and when a variable is deferred, "why?" has a single place to look.
 *
 * ### Why an absent declaration defers nothing
 *
 * Deferring a statement that is actually *ours* is the expensive mistake: the
 * `{% if %}` reaches the recipient's inbox as literal Twig, and any
 * `{{ theme.… }}` inside it with it. Getting it wrong the other way merely
 * restores the behaviour from before send-time conditionals existed.
 *
 * So the question is "**is this root known to be the provider's?**", and with no
 * declaration nothing is known — every condition stays at push time. That is why
 * the declaration is `string[] | undefined` and never defaults to `[]`: an empty
 * list is the claim that *every* namespace is the provider's, which is the
 * dangerous direction.
 */
import { EmailVariableGroup } from '../types';
import { Condition } from './document';

/** The root of a dotted path: `customer.firstname` → `customer`. */
export function pathRoot(path: string): string {
  return path.trim().split('.')[0] ?? '';
}

/**
 * The roots the provider resolves: what the picker offers, minus what the
 * server declared as ours.
 *
 * A catalogue namespace may itself be dotted (`email_config.template_vars`), so
 * only its root is compared — that is the part a written variable path starts
 * with.
 *
 * Empty when `renderTimeNamespaces` is undefined, so nothing is deferred until
 * the server has actually said which is which.
 */
export function sendTimeRoots(
  groups: EmailVariableGroup[] = [],
  renderTimeNamespaces?: string[],
): Set<string> {
  const roots = new Set<string>();
  if (!renderTimeNamespaces) return roots;

  const ours = new Set(renderTimeNamespaces.map(pathRoot));
  for (const group of groups) {
    const root = pathRoot(group.namespace);
    if (root && !ours.has(root)) roots.add(root);
  }
  return roots;
}

/** True when this variable is filled by the provider, per recipient. */
export function isSendTimePath(path: string, roots: Set<string>): boolean {
  return roots.has(pathRoot(path));
}

/**
 * Which render a condition belongs to, or `'mixed'` when it names both.
 *
 * A mixed condition cannot be expressed: one `{% if %}` is evaluated in one
 * pass or the other, and either choice breaks half of it. The caller warns and
 * keeps it at push time — the failure there is a block that may be wrongly
 * hidden, against literal Twig in an inbox.
 */
export function conditionScope(
  condition: Condition,
  roots: Set<string>,
): 'render' | 'send' | 'mixed' {
  let send = false;
  let render = false;

  for (const clause of condition.clauses) {
    if (!clause.path.trim()) continue;
    if (isSendTimePath(clause.path, roots)) send = true;
    else render = true;
  }

  if (send && render) return 'mixed';
  return send ? 'send' : 'render';
}

/**
 * A simple variable path with optional filters, and nothing else.
 *
 * The frontend twin of `SendTimeTags::OUTPUT_TAG`, and deliberately as narrow:
 * an expression with an operator in it (`a ? b : c`, `a ~ b`) is our own logic
 * far more often than it is a merge tag, and a leading quote
 * (`'key'|trans`) is a saved text, which we resolve.
 */
const SIMPLE_PATH = /^([A-Za-z_]\w*)((?:\.\w+)*)\s*(\|[^{}]*)?$/;

/**
 * True when this embedded expression is one variable the provider fills.
 *
 * Used to decide how a *value* carries it — see `emitValue` in compile.ts. Only
 * a bare path qualifies, so anything the render might legitimately compute stays
 * with the render.
 */
export function isSendTimeExpression(
  expression: string,
  roots: Set<string>,
): boolean {
  const match = SIMPLE_PATH.exec(expression.trim());

  return match ? roots.has(match[1]) : false;
}

/**
 * A Twig statement our render must emit as text rather than run.
 *
 * Twig 3 spells it `verbatim`; `raw` was the Twig 1 name and is gone. twig.js —
 * the preview's engine — understands it too, which is what lets the preview
 * unwrap the statement and then evaluate it against the author's sample data
 * like any other provider-side conditional.
 */
export function deferStatement(statement: string): string {
  return `{% verbatim %}${statement}{% endverbatim %}`;
}
